(function(algolia) {
  'use strict';
  var rangeSlider = algolia.externals.widgets.rangeSlider;
  var menu = algolia.externals.widgets.menu;
  var refinementList = algolia.externals.widgets.refinementList;
  var translations = algolia.translations;

  /**
   * Custom min/max price widget.
   *
   * The bundled Algolia build (algolia_externals.js) only exports the
   * `rangeSlider` widget - `rangeInput` and the `connectRange`/
   * `connectNumericMenu` connectors were never included, so a prebuilt
   * min/max widget isn't available. This talks to the underlying
   * algoliasearch-helper directly (addNumericRefinement/removeNumericRefinement
   * + helper.search()) instead, which needs no extra widget/connector.
   *
   * Declares the attribute as a disjunctive facet via
   * getWidgetSearchParameters so Algolia keeps returning
   * disjunctiveFacetsStats (used for the dynamic min/max placeholders) and
   * so the existing panel()-based "hide facet with no values" logic in
   * algolia_instant_search.js.liquid keeps working unchanged.
   */
  var priceMinMax = function priceMinMax(params) {
    var containerSelector = params.container;
    var attribute = params.attribute;
    var DEBOUNCE_MS = 500;
    var container, minInput, maxInput, debounceTimer, currentHelper;

    var getCurrencyCode = function () {
      try {
        return (algolia.helpers.activeCurrencyCode() || '').toUpperCase();
      } catch (e) {
        return '';
      }
    };

    var applyRefinements = function () {
      if (!currentHelper) return;
      currentHelper.removeNumericRefinement(attribute, '>=');
      currentHelper.removeNumericRefinement(attribute, '<=');

      var minVal = minInput.value.trim();
      var maxVal = maxInput.value.trim();

      if (minVal !== '' && !isNaN(minVal)) {
        currentHelper.addNumericRefinement(attribute, '>=', Number(minVal));
      }
      if (maxVal !== '' && !isNaN(maxVal)) {
        currentHelper.addNumericRefinement(attribute, '<=', Number(maxVal));
      }
      currentHelper.search();
    };

    var scheduleApply = function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(applyRefinements, DEBOUNCE_MS);
    };

    // Keep the two inputs in sync with the helper's current refinement state
    // (e.g. after "Clear all", or on load from a routed URL) without
    // clobbering a field the shopper is actively typing in.
    var syncFromHelper = function (helper) {
      if (!helper || !minInput || !maxInput) return;
      if (document.activeElement !== minInput) {
        var existingMin = helper.getNumericRefinement(attribute, '>=');
        minInput.value = existingMin && existingMin.length ? existingMin[0] : '';
      }
      if (document.activeElement !== maxInput) {
        var existingMax = helper.getNumericRefinement(attribute, '<=');
        maxInput.value = existingMax && existingMax.length ? existingMax[0] : '';
      }
    };

    return {
      $$type: 'custom.priceMinMax',
      $$widgetType: 'ais.priceMinMax',

      init: function (initOptions) {
        currentHelper = initOptions.helper;
        container = typeof containerSelector === 'string'
          ? document.querySelector(containerSelector)
          : containerSelector;
        if (!container) return;

        var currencyCode = getCurrencyCode();
        var isAr = (algolia && typeof algolia.isArabic === 'function')
          ? algolia.isArabic()
          : Boolean((window.algoliaShopify && (window.algoliaShopify.isArabicMode === true || (typeof window.algoliaShopify.isArabic === 'boolean' && window.algoliaShopify.isArabic))) || document.documentElement.lang === 'ar' || (window.Shopify && window.Shopify.locale === 'ar') || window.location.pathname.startsWith('/ar/'));
        var minLabel = (algolia.translations && algolia.translations.minPrice) || (isAr ? 'أدنى سعر' : 'Min Price');
        var maxLabel = (algolia.translations && algolia.translations.maxPrice) || (isAr ? 'أقصى سعر' : 'Max Price');

        container.innerHTML =
          '<div class="ais-PriceRange">' +
            '<div class="ais-PriceRange-field">' +
              '<label class="ais-PriceRange-label">' + minLabel + '</label>' +
              '<div class="ais-PriceRange-inputWrap">' +
                '<span class="ais-PriceRange-currency">' + currencyCode + '</span>' +
                '<input type="number" inputmode="decimal" min="0" step="any" class="ais-PriceRange-input ais-PriceRange-input--min" />' +
              '</div>' +
            '</div>' +
            '<div class="ais-PriceRange-field">' +
              '<label class="ais-PriceRange-label">' + maxLabel + '</label>' +
              '<div class="ais-PriceRange-inputWrap">' +
                '<span class="ais-PriceRange-currency">' + currencyCode + '</span>' +
                '<input type="number" inputmode="decimal" min="0" step="any" class="ais-PriceRange-input ais-PriceRange-input--max" />' +
              '</div>' +
            '</div>' +
          '</div>';

        minInput = container.querySelector('.ais-PriceRange-input--min');
        maxInput = container.querySelector('.ais-PriceRange-input--max');

        minInput.addEventListener('input', scheduleApply);
        maxInput.addEventListener('input', scheduleApply);

        syncFromHelper(currentHelper);
      },

      render: function (renderOptions) {
        currentHelper = renderOptions.helper;
        if (!container || !minInput || !maxInput) return;

        var results = renderOptions.results;
        var facetStats =
          (results && results.disjunctiveFacetsStats && results.disjunctiveFacetsStats[attribute]) ||
          (results && results.facets_stats && results.facets_stats[attribute]);

        if (facetStats) {
          if (typeof facetStats.min === 'number') {
            minInput.placeholder = Math.floor(facetStats.min).toLocaleString('en');
          }
          if (typeof facetStats.max === 'number') {
            maxInput.placeholder = Math.ceil(facetStats.max).toLocaleString('en');
          }
        }

        syncFromHelper(currentHelper);
      },

      dispose: function () {
        clearTimeout(debounceTimer);
        if (minInput) minInput.removeEventListener('input', scheduleApply);
        if (maxInput) maxInput.removeEventListener('input', scheduleApply);
      },

      getWidgetSearchParameters: function (searchParameters) {
        return searchParameters.addDisjunctiveFacet(attribute);
      },
    };
  };

  var TYPES_TO_WIDGET = {
    slider: { name: 'priceMinMax', useDefault: true, widget: priceMinMax },
    menu: { name: 'menu', params: { limit: 10 }, widget: menu },
    conjunctive: {
      name: 'refinementList',
      params: { operator: 'and', limit: 10 },
      widget: refinementList,
    },
    disjunctive: {
      name: 'refinementList',
      params: { operator: 'or', limit: 10 },
      widget: refinementList,
    },
  };

  var sortByRefined = function sortByRefined(sortFunction) {
    return function(a, b) {
      if (a.refined !== b.refined) {
        if (a.refined) {
          return -1;
        }
        if (b.refined) {
          return 1;
        }
      }
      return sortFunction(a, b);
    };
  };

  /*
   * Sorting functions : Allows to chose in which order you want to display the facets
   * Algolia will always send you back the most relevant values for each facet (the ones
   * with the highest count). These sorting functions won't change which results come back
   * but how they are displayed. To retrieve more results, change the maxValuesPerFacet
   * parameter in your Algolia Dashboard.
   * The default sort function is refined > count > name.
   */
  algolia.facetSortFunctions = {
    price_range: sortByRefined(function sortRanges(a, b) {
      if (a.name.length === b.name.length) {
        return a.name.localeCompare(b.name);
      }
      return a.name.length - b.name.length;
    }),
  };

  /*
   * Display functions
   * When the object sent back for a facet item doesn't match how you would want it to look
   * like, use a function to reformat it how you want.
   */
  algolia.facetDisplayFunctions = {
    price_range: function displayRange(value) {
      var values = value.split(':');

      return values
        .map(e => {
          return algolia.formatMoney(Number(e) * 100).replace(/\.\d+/, '');
        })
        .join(' - ');
    },
  };

  algolia.facetCssClasses = {
    root: 'ais-facet',
    header: 'ais-facet--header',
    body: 'ais-facet--body',
    item: 'ais-facet--item',
    label: 'ais-facet--label',
    checkbox: 'ais-facet--checkbox',
    active: 'ais-facet--active',
    count: Boolean(algolia.config.show_products) ? 'ais-facet--count-distinct' : 'ais-facet--count',
    noResults: 'ais-facet--noResults',
  };

  // ── Arabic facet swap map ────────────────────────────────────────────────
  // On AR locale, swap English facet attributes to their *_ar equivalents
  // and translate the facet title. vendor and price are kept as-is (no AR
  // field exists for vendor; price is numeric).
  var AR_FACET_MAP = {
    'meta.custom.gender':         { name: 'meta.custom.gender_ar',        title: 'الجنس' },
    'meta.custom.movement':       { name: 'meta.custom.movement_ar',       title: 'الحركة' },
    'meta.custom.strap_material': { name: 'meta.custom.strap_material_ar', title: 'مادة السوار' },
    'meta.custom.dial_color':     { name: 'meta.custom.dial_color_ar',     title: 'لون الوجه' },
    'meta.custom.case_shape':     { name: 'meta.custom.case_shape_ar',     title: 'شكل الهيكل' },
    'meta.custom.case_size_ar':   { name: 'meta.custom.case_size_ar',      title: 'حجم الهيكل' },
    'options.case_size':          { name: 'options.case_size',             title: 'حجم الهيكل' },
    'product_type':               { name: 'meta.custom.product_type_ar',   title: 'نوع المنتج' },
    'vendor':                     { name: 'vendor',                        title: 'ماركة' },
    'price':                      { name: 'price',                         title: 'السعر' },
  };
  var isArabicLocale = function () {
    return Boolean(
      (window.algoliaShopify && window.algoliaShopify.isArabicMode === true) ||
      (window.algoliaShopify && typeof window.algoliaShopify.isArabic === 'boolean' && window.algoliaShopify.isArabic) ||
      document.documentElement.lang === 'ar' ||
      document.documentElement.getAttribute('lang') === 'ar' ||
      (window.Shopify && window.Shopify.locale === 'ar') ||
      window.location.pathname.startsWith('/ar/') ||
      window.location.pathname === '/ar'
    );
  };
  algolia.isArabic = isArabicLocale;
  algolia.isArabicLocale = isArabicLocale;
  var IS_AR = isArabicLocale();

  var applyArFacet = function(facet) {
    if (!isArabicLocale()) return facet;
    var ar = AR_FACET_MAP[facet.name];
    if (!ar) return facet;
    return Object.assign({}, facet, { name: ar.name, title: ar.title });
  };
  // ── End Arabic facet swap map ────────────────────────────────────────────

  var enabledFacets = algolia.config.facets.filter(facet => {
    return facet.enabled || parseInt(facet.enabled, 10);
  });
  algolia.facets = enabledFacets.map(facet => {
    var mapped = applyArFacet(facet);
    return {
      ...mapped,
      escapedName: encodeURIComponent(mapped.name),
    };
  });
  algolia.shownFacets = algolia.facets.filter(facet => {
    return facet.type !== 'hidden';
  });
  algolia.hiddenFacets = algolia.config.facets.filter(facet => {
    return facet.type === 'hidden';
  });

  var facetToWidget = function(facet) {
    var widget = TYPES_TO_WIDGET[facet.type];
    var params = algolia.assign({}, widget.params);
    if (facet.limit !== undefined && facet.limit !== null) {
      // No "show more" toggle anywhere - render every value up front and
      // let the (scrollable) panel handle overflow instead.
      params.limit = 100;
      params.showMore = false;
    }
    params.container =
      "[class~='ais-facet-" + facet.escapedName + "-container']";
    params.attribute = facet.name;
    params.templates = {};
    params.cssClasses = algolia.facetCssClasses;

    if (facet.searchable) {
      var isAr = isArabicLocale();
      params.searchable = true;
      params.searchablePlaceholder = (algolia.translations && algolia.translations.searchForFacets ? algolia.translations.searchForFacets + ' ' : (isAr ? 'ابحث في ' : 'Search ')) + facet.title;
      params.templates.searchableReset = '<span class="ais-SearchBox-resetText">' + (isAr ? 'مسح' : 'Clear') + '</span>';
      params.templates.searchableNoResults =
        '<div class="ais-facet--noResults-message">' +
          '<p class="ais-facet--noResults-title">' + (isAr ? 'لا توجد نتائج' : 'No results found') + '</p>' +
          '<p class="ais-facet--noResults-subtitle">' + (isAr ? 'لم نتمكن من إيجاد نتائج، حاول البحث بكلمة أخرى.' : 'We couldn\'t find this ' + facet.title.toLowerCase() + ', Try searching for another.') + '</p>' +
        '</div>';
    }

    params.templates.header = function() {
      return facet.title;
    };

    if (!widget.useDefault) {
      params.templates.item = (item, {html}) => {
        return algolia.instantSearchFacetItem(item, html)
      }
    }

    if (algolia.facetSortFunctions[facet.name]) {
      params.sortBy = algolia.facetSortFunctions[facet.name];
    } else if (widget.name === 'menu' || widget.name === 'refinementList') {
      // Show all facet values alphabetically ascending instead of Algolia's
      // default most-popular-first (count desc) ordering.
      params.sortBy = ['name:asc'];
    }

    var displayFunction = algolia.facetDisplayFunctions[facet.name];
    params.transformItems = function(items) {       
      return items.map(item => ({
        ...item,
        type: facet.type,        
        label: displayFunction ? displayFunction(item.value) : item.value
      }));
    }

    return {
      name: widget.name,
      params: params,
      widget: widget.widget,
    };
  };

  // try to fetch facets for current collection or fallback to collections default
  var collection_facets =
    algolia.current_collection_id &&
    algolia.config.collection_facets &&
    algolia.config.collection_facets[algolia.current_collection_id]
      ? algolia.config.collection_facets[algolia.current_collection_id]
      : algolia.config.collection_facets &&
        algolia.config.collection_facets.default;

  if (collection_facets) {
    var enabledCollectionFacets = collection_facets.filter(facet => {
      return facet.enabled || parseInt(facet.enabled, 10);
    });

    algolia.collectionFacets = enabledCollectionFacets.map(facet => {
      var mapped = applyArFacet(facet);
      return {
        ...mapped,
        escapedName: encodeURIComponent(mapped.name)
      };
    });
    algolia.collectionShownFacets = algolia.collectionFacets.filter(facet => {
      return facet.type !== 'hidden';
    });
    algolia.collectionHiddenFacets = collection_facets.filter(facet => {
      return facet.type === 'hidden';
    });
    algolia.collectionFacetsWidgets = algolia.collectionShownFacets.map(facetToWidget);
  }
  algolia.facetsWidgets = algolia.shownFacets.map(facetToWidget);

  // Console logging for facets setup (must be inside IIFE where isArabicLocale is defined)
  console.log('%c[Algolia Facets Setup] Locale is Arabic?', 'color: #27ae60; font-weight: bold;', isArabicLocale());
  console.log('%c[Algolia Facets Setup] Search Facets (algolia.facets):', 'color: #27ae60; font-weight: bold;', algolia.facets);
  if (typeof collection_facets !== 'undefined' && collection_facets) {
    console.log('%c[Algolia Facets Setup] Collection Facets Raw:', 'color: #27ae60; font-weight: bold;', collection_facets);
    console.log('%c[Algolia Facets Setup] Collection Facets Mapped (algolia.collectionFacets):', 'color: #27ae60; font-weight: bold;', algolia.collectionFacets);
  }
})(window.algoliaShopify);